import { ReactNode } from 'react';
import { FLOATING_ALERT_DEFAULT_TIMING } from '../../../constants/times';

export type LooseAutocomplete<T extends string> = T | Omit<string, T>;

export interface Alert {
  id?: string;
  timestamp?: number;
  sticky?: boolean;
  timeout?: number;
  type?: LooseAutocomplete<'info' | 'warning' | 'danger' | 'success' | 'tip'>;
  onClose?: (...args: Array<any>) => void;
  titleText?: ReactNode;
  message?: ReactNode;
  [extraAlertProps: string]: any;
}
export interface AlertsObject {
  [alertKey: string]: Alert;
}

export interface SubscriptionsObject {
  [key: string]: (alertArray: Array<Alert>) => void;
}

export interface AlertStore {
  newFloatingAlertStore(): AlertStore;
  getAlerts(): AlertsObject;
  getSubscriptions(): SubscriptionsObject;
  subscribe(
    key: string,
    updateCallback: (alertArray: Array<Alert>) => void
  ): void;
  unsubscribe(key: string): void;
  addAlert(alert: Alert): void;
  removeAlert(id: string): void;
}

const newFloatingAlertStore = (): AlertStore => {
  let alertId = 0;
  let alerts: AlertsObject = {};
  let subscriptions: SubscriptionsObject = {};

  const alertsToArray = (): Array<Alert> => {
    return Object.keys(alerts).map((alertKey): Alert => alerts[alertKey]);
  };

  const setAlerts = (nextAlerts: AlertsObject): void => {
    alerts = nextAlerts;

    const alertArray: Array<Alert> = alertsToArray();

    Object.keys(subscriptions).forEach((key): void => {
      subscriptions[key](alertArray);
    });
  };

  return {
    newFloatingAlertStore,

    getAlerts(): AlertsObject {
      return alerts;
    },

    getSubscriptions(): SubscriptionsObject {
      return subscriptions;
    },

    subscribe(
      key: string,
      updateCallback: (alertArray: Array<Alert>) => void
    ): void {
      subscriptions[key] = updateCallback;
      updateCallback(alertsToArray());
    },

    unsubscribe(key: string): void {
      const newSubscriptions: SubscriptionsObject = { ...subscriptions };
      delete newSubscriptions[key];
      subscriptions = newSubscriptions;
    },

    addAlert({
      id = `Store-${alertId++}`,
      sticky,
      timeout,
      timestamp = Date.now(),
      type,
      onClose,
      ...alert
    }: Alert): void {
      const newAlert: Alert = {
        ...alert,
        id,
        onClose: (...args) => {
          if (onClose) onClose(...args);
          this.removeAlert(id);
        },
        timestamp,
        type,
      };

      setAlerts({
        ...alerts,
        [id]: newAlert,
      });

      if (!sticky && type !== 'danger') {
        const defaultTimeout: number = parseInt(
          FLOATING_ALERT_DEFAULT_TIMING,
          10
        );
        const alertTimeout: number = timeout != null ? timeout : defaultTimeout;

        setTimeout(() => {
          this.removeAlert(id);
        }, alertTimeout);
      }
    },

    removeAlert(id: string): void {
      const newAlerts: AlertsObject = { ...alerts };
      delete newAlerts[id];

      setAlerts(newAlerts);
    },
  };
};

// Workaround to avoid mixed exports, but still have access to a generator
// function for ease of testing.
export const FloatingAlertStore: AlertStore = newFloatingAlertStore();
